import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  fetchIntegrations,
  createIntegration as createIntegrationAPI,
  updateIntegration as updateIntegrationAPI,
  deleteIntegration as deleteIntegrationAPI,
} from '_/api/integrations/integrations.api';
import { ValidationError } from '_/common/types/errors.types';
import { notification } from 'antd';

export const INTEGRATIONS_QUERY_KEY = (orgSlug: string): string[] => ['integrations', orgSlug];

export const useIntegrations = (orgSlug?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: INTEGRATIONS_QUERY_KEY(orgSlug || ''),
    queryFn: () => fetchIntegrations(orgSlug!),
    enabled: !!orgSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useIntegration = (orgSlug: string, integrationId?: string) => {
  const { data: integrations = [], isLoading } = useIntegrations(orgSlug);

  const integration = integrations.find((i) => i.id === integrationId);

  return {
    data: integration,
    isLoading: isLoading,
  };
};

export const useCreateIntegration = (orgSlug: string): UseMutationResult<any, ValidationError, any> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: (integration: any) => createIntegrationAPI(integration),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY(orgSlug) });
      notification.success({
        message: 'Integration Created',
      });
      void history({ to: `/orgs/${orgSlug}/integrations` });
    },
    onError: (error: any) => {
      console.error('Error creating integration', error);

      // Convert error to ValidationError if it has messages
      const validationError = error.messages
        ? new ValidationError(error.messages)
        : new ValidationError([{ type: 'general', error: error.message || 'Error creating integration' }]);

      notification.error({
        message: validationError.messages[0]?.error || 'Error creating integration',
      });

      throw validationError;
    },
  });
};

export const useUpdateIntegration = (
  orgSlug: string,
): UseMutationResult<any, ValidationError, { integrationId: string; updates: any }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ integrationId, updates }: { integrationId: string; updates: any }) =>
      updateIntegrationAPI(integrationId, updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY(orgSlug) });
      notification.success({
        message: 'Integration Updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating integration', error);

      // Convert error to ValidationError if it has messages
      const validationError = error.messages
        ? new ValidationError(error.messages)
        : new ValidationError([{ type: 'general', error: error.message || 'Error updating integration' }]);

      notification.error({
        message: validationError.messages[0]?.error || 'Error updating integration',
      });

      throw validationError;
    },
  });
};

export const useDeleteIntegration = (orgSlug: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) => deleteIntegrationAPI(integrationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY(orgSlug) });
      notification.success({
        message: 'Integration Deleted',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting integration', error);
      notification.error({
        message: error.messages?.[0] || 'Error deleting integration',
      });
    },
  });
};
